import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useRef } from "react";
import { z } from "zod";
import { SiteHeader } from "@/components/SiteHeader";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const Route = createFileRoute("/warranty/claim")({
  head: () => ({
    meta: [
      { title: "Submit a Warranty Claim — Double Lion" },
      { name: "description", content: "Submit a warranty claim for your Double Lion binoculars. Include your serial number, contact details, photos, and a description of the issue." },
      { property: "og:title", content: "Submit a Warranty Claim — Double Lion" },
      { property: "og:description", content: "File a warranty claim for your Double Lion instrument." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: WarrantyClaimPage,
});

const MAX_FILES = 6;
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB

const claimSchema = z.object({
  serial_number: z.string().trim().min(1, "Serial number is required").max(100),
  full_name: z.string().trim().min(1, "Name is required").max(120),
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  purchase_date: z.string().optional().or(z.literal("")),
  description: z.string().trim().min(20, "Please describe the issue (20+ characters)").max(5000),
});

function WarrantyClaimPage() {
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFilesSelected(selected: FileList | null) {
    if (!selected) return;
    const incoming = Array.from(selected);
    const valid: File[] = [];
    for (const f of incoming) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} exceeds 8MB`);
        continue;
      }
      valid.push(f);
    }
    const next = [...files, ...valid].slice(0, MAX_FILES);
    if (files.length + valid.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} images`);
    }
    setFiles(next);
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});

    const fd = new FormData(e.currentTarget);
    const raw = {
      serial_number: String(fd.get("serial_number") || ""),
      full_name: String(fd.get("full_name") || ""),
      email: String(fd.get("email") || ""),
      phone: String(fd.get("phone") || ""),
      purchase_date: String(fd.get("purchase_date") || ""),
      description: String(fd.get("description") || ""),
    };

    const parsed = claimSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as string;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      toast.error("Please fix the highlighted fields");
      return;
    }

    setSubmitting(true);
    try {
      // Upload images
      const claimId = crypto.randomUUID();
      const imageUrls: string[] = [];

      const allowedExts = ["jpg", "jpeg", "png", "webp", "heic"] as const;
      const mimeToExt: Record<string, string> = {
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/png": "png",
        "image/webp": "webp",
        "image/heic": "heic",
        "image/heif": "heic",
      };
      for (const file of files) {
        const rawExt = (file.name.split(".").pop() || "").toLowerCase();
        const normalizedExt = rawExt === "jpeg" ? "jpg" : rawExt;
        const safeExt = (allowedExts as readonly string[]).includes(normalizedExt)
          ? normalizedExt
          : mimeToExt[file.type] || "jpg";
        const path = `${claimId}/${crypto.randomUUID()}.${safeExt}`;
        const { error: upErr } = await supabase.storage
          .from("warranty-claims")
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;
        imageUrls.push(path);
      }

      const { error: insErr } = await supabase.from("warranty_claims").insert({
        id: claimId,
        serial_number: parsed.data.serial_number,
        full_name: parsed.data.full_name,
        email: parsed.data.email,
        phone: parsed.data.phone || null,
        purchase_date: parsed.data.purchase_date || null,
        description: parsed.data.description,
        image_urls: imageUrls,
      });
      if (insErr) throw insErr;

      // Notify the service team (non-blocking for the customer's success state)
      try {
        await fetch("/api/public/warranty-notify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ claimId }),
        });
      } catch {
        // Claim is already saved; notification failures are logged server-side.
      }

      setSubmitted(true);
      setFiles([]);
      toast.success("Warranty claim submitted");
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "";
      const isNetwork =
        !raw ||
        /failed to fetch|network|load failed|typeerror/i.test(raw);
      toast.error(
        isNetwork
          ? "We couldn't reach our servers. Please try again, or email info@doublelion.com."
          : raw,
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SiteHeader />
        <main className="pt-32 pb-32 px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              Claim received
            </p>
            <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight">
              Thank you.<br />
              <em className="italic font-light">We'll be in touch.</em>
            </h1>
            <p className="mt-8 text-muted-foreground leading-relaxed">
              Your warranty claim has been submitted. A service team member will respond to the email you provided within three business days.
            </p>
            <div className="mt-12 flex items-center justify-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center justify-center px-8 py-4 bg-foreground text-background font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-foreground/90 transition-colors"
              >
                Return home
              </Link>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground transition-colors"
              >
                Submit another
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-32 px-6">
        <div className="mx-auto max-w-3xl">
          <Link
            to="/warranty"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Warranty
          </Link>

          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Warranty Claim
          </p>
          <h1 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Submit a claim
          </h1>
          <p className="mt-6 text-muted-foreground leading-relaxed max-w-xl">
            Provide your serial number, contact details, and a few photos of the issue. Our service team will respond within three business days.
          </p>

          <form onSubmit={onSubmit} className="mt-16 space-y-12">
            {/* Instrument */}
            <Fieldset label="01 — Instrument">
              <Field
                label="Serial number"
                name="serial_number"
                required
                placeholder="DL87654321"
                error={errors.serial_number}
              />
              <Field
                label="Purchase date"
                name="purchase_date"
                type="date"
                error={errors.purchase_date}
              />
            </Fieldset>

            {/* Contact */}
            <Fieldset label="02 — Contact">
              <Field label="Full name" name="full_name" required error={errors.full_name} />
              <Field label="Email" name="email" type="email" required error={errors.email} />
              <Field label="Phone (optional)" name="phone" type="tel" error={errors.phone} />
            </Fieldset>

            {/* Issue */}
            <Fieldset label="03 — Describe the issue">
              <div className="md:col-span-2">
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
                  Description <span className="text-foreground">*</span>
                </label>
                <textarea
                  name="description"
                  rows={6}
                  required
                  maxLength={5000}
                  placeholder="Describe the defect, when it started, and how it affects use."
                  className="w-full bg-transparent border border-border focus:border-foreground outline-none px-4 py-3 text-base resize-y transition-colors"
                />
                {errors.description && (
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-destructive">
                    {errors.description}
                  </p>
                )}
              </div>
            </Fieldset>

            {/* Photos */}
            <Fieldset label="04 — Photos">
              <div className="md:col-span-2">
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
                  Upload up to {MAX_FILES} images (max 8MB each)
                </label>

                <div
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                  }}
                  role="button"
                  tabIndex={0}
                  className="border border-dashed border-border hover:border-foreground transition-colors px-6 py-12 text-center cursor-pointer"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
                    Click to add photos
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground/70">
                    JPG, PNG, HEIC — {files.length}/{MAX_FILES} added
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handleFilesSelected(e.target.files);
                    e.target.value = "";
                  }}
                />

                {files.length > 0 && (
                  <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {files.map((file, idx) => {
                      const url = URL.createObjectURL(file);
                      return (
                        <li key={`${file.name}-${idx}`} className="relative group aspect-square overflow-hidden bg-muted">
                          <img
                            src={url}
                            alt={file.name}
                            className="size-full object-cover"
                            onLoad={() => URL.revokeObjectURL(url)}
                          />
                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            aria-label={`Remove ${file.name}`}
                            className="absolute top-2 right-2 size-8 inline-flex items-center justify-center bg-background/90 text-foreground text-xs font-mono uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </Fieldset>

            <div className="pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground max-w-sm">
                By submitting, you confirm the information is accurate to the best of your knowledge.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center px-10 py-4 bg-foreground text-background font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting…" : "Submit claim"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function Fieldset({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 md:gap-12 border-t border-border pt-12">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        {label}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>
    </section>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  placeholder,
  error,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  error?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
        {label} {required && <span className="text-foreground">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-border focus:border-foreground outline-none px-0 py-2 text-base transition-colors"
      />
      {error && (
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-destructive">{error}</p>
      )}
    </div>
  );
}
